"use client"

import { useState, useEffect, useCallback } from "react"
import { isScrollToTopVisible } from "@/lib/scrollFabVisibility"

const PAGE_CONTENT_ID = "page-content"

const scrollToContentButtonClass =
  "flex h-11 w-11 items-center justify-center rounded-full bg-blue-trmb text-white shadow-lg transition-[opacity,bottom,transform] duration-300 ease-in-out hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-trmb focus-visible:ring-offset-2 cursor-pointer"

function getHeroElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    '[data-testid="hero-lead"][data-page-first-hero]',
  )
}

function isFirstSectionInPageContent(hero: HTMLElement): boolean {
  const pageContent = document.getElementById(PAGE_CONTENT_ID)
  if (!pageContent) return false

  const sectionWrapper = hero.parentElement
  if (!sectionWrapper || !pageContent.contains(sectionWrapper)) return false

  const sectionNodes = Array.from(pageContent.children).filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement && Boolean(child.id),
  )

  return sectionNodes[0] === sectionWrapper
}

function getHeroScrollBottom(hero: HTMLElement): number {
  return hero.getBoundingClientRect().bottom + window.scrollY
}

export const HeroScrollToContent = () => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const hero = getHeroElement()
    if (!hero || !isFirstSectionInPageContent(hero)) return

    let ticking = false
    const updateVisibility = () => {
      const heroBottom = getHeroScrollBottom(hero)
      const withinHero = window.scrollY < heroBottom - 1
      setVisible(withinHero && !isScrollToTopVisible())
    }

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateVisibility()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    updateVisibility()

    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [])

  const scrollToContent = useCallback(() => {
    const hero = getHeroElement()
    if (!hero) return

    const sectionWrapper = hero.parentElement
    const nextSection = sectionWrapper?.nextElementSibling

    if (nextSection instanceof HTMLElement) {
      nextSection.scrollIntoView({ behavior: "smooth", block: "start" })
      return
    }

    window.scrollTo({
      top: getHeroScrollBottom(hero),
      behavior: "smooth",
    })
  }, [])

  return (
    <div className={`absolute bottom-0 flex flex-row justify-center w-full ${visible ? "opacity-100" : "pointer-events-none opacity-0"}`}>
      <button
        type="button"
        onClick={scrollToContent}
        aria-label="Scroll to content"
        className={`fixed bottom-6 z-50 ${scrollToContentButtonClass} ${visible ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  )
}
